import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

export const useSocket = (eventId, handlers = {}) => {
  const socketRef = useRef(null);

  useEffect(() => {
    // Initialize socket connection with fallback to polling
    socketRef.current = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      path: '/socket.io/',
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      if (eventId) {
        socket.emit('join_event', { event_id: eventId });
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    // Register event handlers
    if (handlers.onEventUpdated) {
      socket.on('event_updated', handlers.onEventUpdated);
    }
    if (handlers.onPhaseCreated) {
      socket.on('phase_created', handlers.onPhaseCreated);
    }
    if (handlers.onPhaseUpdated) {
      socket.on('phase_updated', handlers.onPhaseUpdated);
    }
    if (handlers.onPhaseDeleted) {
      socket.on('phase_deleted', handlers.onPhaseDeleted);
    }
    if (handlers.onPhasesReordered) {
      socket.on('phases_reordered', handlers.onPhasesReordered);
    }
    if (handlers.onTimerAction) {
      socket.on('timer_action', handlers.onTimerAction);
    }
    if (handlers.onCommentCreated) {
      socket.on('comment_created', handlers.onCommentCreated);
    }
    if (handlers.onCommentDeleted) {
      socket.on('comment_deleted', handlers.onCommentDeleted);
    }
    if (handlers.onCommentUpdated) {
      socket.on('comment_updated', handlers.onCommentUpdated);
    }
    if (handlers.onScreensUpdated) {
      socket.on('screens_updated', handlers.onScreensUpdated);
    }
    if (handlers.onSpeakerCreated) {
      socket.on('speaker_created', handlers.onSpeakerCreated);
    }
    if (handlers.onSpeakerUpdated) {
      socket.on('speaker_updated', handlers.onSpeakerUpdated);
    }
    if (handlers.onSpeakerDeleted) {
      socket.on('speaker_deleted', handlers.onSpeakerDeleted);
    }

    return () => {
      if (eventId) {
        socket.emit('leave_event', { event_id: eventId });
      }
      socket.disconnect();
    };
  }, [eventId]);

  const joinEvent = useCallback((id) => {
    if (socketRef.current) {
      socketRef.current.emit('join_event', { event_id: id });
    }
  }, []);

  const leaveEvent = useCallback((id) => {
    if (socketRef.current) {
      socketRef.current.emit('leave_event', { event_id: id });
    }
  }, []);

  return { socket: socketRef.current, joinEvent, leaveEvent };
};

export default useSocket;
